import { Fragment } from 'react';

/** Lightweight, dependency-free diagrams for the user guide. Plain markup + Bootstrap utilities
 *  (no charting/mermaid library) so they render instantly, scale with the page, and stay
 *  readable by screen readers: each diagram is an ordered list with a text label. */

export type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'info';

export interface FlowNode {
  title: string;
  sub?: string;
  tone?: Tone;
}

function Box({ node }: { node: FlowNode }) {
  const tone = node.tone ?? 'secondary';
  return (
    <div
      className={`border border-${tone} rounded px-3 py-2 bg-${tone}-subtle text-center`}
      style={{ minWidth: 120, maxWidth: 190 }}
    >
      <div className="fw-semibold small">{node.title}</div>
      {node.sub && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{node.sub}</div>}
    </div>
  );
}

function Arrow({ vertical }: { vertical?: boolean }) {
  return (
    <span aria-hidden="true" className="text-muted px-1 fs-5">
      {vertical ? '↓' : '→'}
    </span>
  );
}

/** A left-to-right sequence of boxes joined by arrows; wraps on narrow screens. */
export function Flow({
  label,
  nodes,
  caption,
  vertical,
}: {
  label: string;
  nodes: FlowNode[];
  caption?: string;
  vertical?: boolean;
}) {
  return (
    <figure className="my-3">
      <ol
        aria-label={label}
        className={`list-unstyled d-flex align-items-center justify-content-center gap-1 mb-1 ${
          vertical ? 'flex-column' : 'flex-wrap'
        }`}
      >
        {nodes.map((n, i) => (
          <Fragment key={n.title}>
            <li>
              <Box node={n} />
            </li>
            {i < nodes.length - 1 && (
              <li aria-hidden="true">
                <Arrow vertical={vertical} />
              </li>
            )}
          </Fragment>
        ))}
      </ol>
      {caption && <figcaption className="text-center text-muted small">{caption}</figcaption>}
    </figure>
  );
}

/** Stacked layers, top = highest precedence / most specific. */
export function Layers({
  label,
  layers,
  caption,
}: {
  label: string;
  layers: (FlowNode & { badge?: string })[];
  caption?: string;
}) {
  return (
    <figure className="my-3">
      <ol aria-label={label} className="list-unstyled mx-auto mb-1" style={{ maxWidth: 560 }}>
        {layers.map((l) => {
          const tone = l.tone ?? 'secondary';
          return (
            <li
              key={l.title}
              className={`border border-${tone} bg-${tone}-subtle rounded px-3 py-2 mb-2 d-flex justify-content-between align-items-center`}
            >
              <span>
                <span className="fw-semibold small">{l.title}</span>
                {l.sub && <span className="text-muted small"> — {l.sub}</span>}
              </span>
              {l.badge && <span className={`badge text-bg-${tone}`}>{l.badge}</span>}
            </li>
          );
        })}
      </ol>
      {caption && <figcaption className="text-center text-muted small">{caption}</figcaption>}
    </figure>
  );
}

/** A day-ruler for the 90-day exception window. */
export function Timeline({ expiryDays, warnDays }: { expiryDays: number; warnDays: number }) {
  const warnAt = expiryDays - warnDays;
  const pct = (d: number) => `${(d / (expiryDays + 20)) * 100}%`;
  return (
    <figure className="my-3">
      <div
        role="img"
        aria-label={`Exception timeline: filed on day 0, warning from day ${warnAt}, expires on day ${expiryDays}`}
        className="position-relative mx-auto"
        style={{ maxWidth: 640, height: 74 }}
      >
        <div className="position-absolute rounded-start bg-success-subtle border border-success" style={{ left: 0, width: pct(warnAt), top: 24, height: 22 }} />
        <div className="position-absolute bg-warning-subtle border border-warning" style={{ left: pct(warnAt), width: `calc(${pct(expiryDays)} - ${pct(warnAt)})`, top: 24, height: 22 }} />
        <div className="position-absolute rounded-end bg-danger-subtle border border-danger" style={{ left: pct(expiryDays), right: 0, top: 24, height: 22 }} />
        {[
          { d: 0, t: 'Filed', a: 'start' },
          { d: warnAt, t: `Day ${warnAt}`, a: 'center' },
          { d: expiryDays, t: `Day ${expiryDays}`, a: 'center' },
        ].map((m) => (
          <div
            key={m.d}
            className="position-absolute small text-muted"
            style={{ left: pct(m.d), top: 50, transform: m.a === 'center' ? 'translateX(-50%)' : undefined }}
          >
            {m.t}
          </div>
        ))}
        <div className="position-absolute small" style={{ left: 0, top: 4 }}>Covered</div>
        <div className="position-absolute small" style={{ left: pct(warnAt), top: 4 }}>Expiring soon</div>
        <div className="position-absolute small" style={{ left: pct(expiryDays), top: 4 }}>Expired → Needs action</div>
      </div>
      <figcaption className="text-center text-muted small">
        The clock starts at the REQ's <strong>filed</strong> date (not the approval date).
      </figcaption>
    </figure>
  );
}
